import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Loader from 'react-loader-spinner';
import { FiInbox } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { useAdminAccess } from '../utilities/adminAccess';
import { champFields } from '../utilities/tournamentsApi';
import {
  CLIENT_FORMS_PAGE_SIZE,
  CLIENT_FORM_STATUSES,
  clientFormContact,
  clientFormProcessedBy,
  clientFormStatusLabelKey,
  clientFormTypeLabelKey,
  extractClientFormsPage,
  formatClientFormDate,
} from '../utilities/clientFormsApi';
import ReorderableTable from '../components/ReorderableTable';
import StadiumCrumbs from '../components/StadiumCrumbs';
import '../styles/stadiumsManagement.css';
import '../styles/tournamentsManagement.css';
import '../styles/clientForms.css';

const statusClass = (status) => {
  if (Number(status) === 1) return 'is-done';
  if (Number(status) === 2) return 'is-rejected';
  return 'is-new';
};

export default function ClientForms() {
  const dispatch = useDispatch();
  const { t, lang } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();
  const { canReadChamp } = useAdminAccess('client_forms');

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(null);
  const [scopeCityId, setScopeCityId] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const requestIdRef = useRef(0);
  const itemsRef = useRef([]);
  itemsRef.current = items;

  useEffect(() => {
    if (!champId && champsLoading) return undefined;
    if (!champId || !canReadChamp) {
      setItems([]);
      setTotal(null);
      setScopeCityId(null);
      setHasMore(false);
      setLoading(false);
      setMessage({
        text: champsLoading ? '' : t(!champId ? 'championshipsLoadError' : 'cfNoAccess'),
        type: champsLoading ? '' : 'error',
      });
      return undefined;
    }
    loadList(0, false);
    return undefined;
  }, [champId, champsLoading, canReadChamp, statusFilter]);

  const loadList = async (offset, append) => {
    const requestId = ++requestIdRef.current;
    if (append) setLoadingMore(true);
    else {
      setLoading(true);
      setMessage({ text: '', type: '' });
    }

    try {
      const body = {
        ...champFields(champId),
        limit: CLIENT_FORMS_PAGE_SIZE,
        offset,
      };
      if (statusFilter !== '') body.status = Number(statusFilter);

      const response = await authorizedPost(dispatch, API_URLS.getAdminClientForms, body);
      if (requestId !== requestIdRef.current) return;

      const page = extractClientFormsPage(response.data);
      const nextItems = append ? itemsRef.current.concat(page.items) : page.items;
      setItems(nextItems);
      setTotal(page.total);
      if (!append) setScopeCityId(page.city_id);
      setHasMore(
        page.total != null
          ? nextItems.length < page.total
          : page.items.length === CLIENT_FORMS_PAGE_SIZE
      );
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      if (!append) {
        setItems([]);
        setTotal(null);
        setScopeCityId(null);
        setHasMore(false);
      }
      setMessage({ text: getApiErrorMessage(error, t('cfLoadError')), type: 'error' });
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  const columns = [
    {
      key: 'date',
      label: t('cfColDate'),
      className: 'cf-cell-date st-cell-muted',
      render: (item) => formatClientFormDate(item.created_at, lang) || t('cfNoValue'),
    },
    {
      key: 'type',
      label: t('cfColType'),
      className: 'cf-cell-type',
      render: (item) => t(clientFormTypeLabelKey(item.form_type)),
    },
    {
      key: 'championship',
      label: t('cfChampionship'),
      className: 'cf-cell-champ st-cell-muted',
      render: (item) => item.championship_name || t('cfNoValue'),
    },
    {
      key: 'name',
      label: t('cfColName'),
      className: 'st-cell-name',
      render: (item) => item.name || t('cfNoValue'),
    },
    {
      key: 'contact',
      label: t('cfColContact'),
      className: 'cf-cell-contact st-cell-muted',
      render: (item) => clientFormContact(item) || t('cfNoValue'),
    },
    {
      key: 'status',
      label: t('cfColStatus'),
      className: 'cf-cell-status',
      render: (item) => (
        <span className={`cf-status ${statusClass(item.status)}`}>
          {t(clientFormStatusLabelKey(item.status))}
        </span>
      ),
    },
    {
      key: 'by',
      label: t('cfColProcessedBy'),
      className: 'cf-cell-by st-cell-muted hide-sm',
      render: (item) => clientFormProcessedBy(item) || t('cfNoValue'),
    },
  ];

  const foundCount = total != null ? total : items.length;

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page cf-list-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={[{ label: t('navClientForms') }]} />
          </div>
        </div>

        <div className="st-toolbar">
          <div className="cf-filters">
            <select
              className="st-select"
              value={statusFilter}
              aria-label={t('cfFilterStatus')}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="">{t('cfFilterAll')} — {t('cfFilterStatus')}</option>
              {CLIENT_FORM_STATUSES.map((item) => (
                <option key={item.value} value={String(item.value)}>{t(item.labelKey)}</option>
              ))}
            </select>
          </div>
          <div className="st-meta">
            <strong>{t('cfFound', { count: foundCount })}</strong>
            {scopeCityId ? <span> · {t('cfScopeCity', { id: scopeCityId })}</span> : null}
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll">
          <div className="st-table-wrap">
            {loading ? (
              <div className="st-loading">
                <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                <p>{t('cfLoading')}</p>
              </div>
            ) : (
              <>
                <ReorderableTable
                  tableClassName="st-table-client-forms"
                  columns={columns}
                  items={items}
                  getRowKey={(item) => item.id}
                  reorderMode={false}
                  getRowHref={(item) => (item.id ? `/client_forms/${item.id}` : null)}
                  empty={(
                    <div className="st-empty">
                      <FiInbox size={48} />
                      <h3>{t('cfEmpty')}</h3>
                      <p>{t('cfEmptyHint')}</p>
                    </div>
                  )}
                />
                {hasMore ? (
                  <div className="cf-load-more">
                    <button
                      type="button"
                      className="st-btn st-btn-secondary"
                      disabled={loadingMore}
                      onClick={() => loadList(items.length, true)}
                    >
                      {loadingMore ? t('cfLoading') : t('dqLoadMore')}
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
